import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useToast } from '../../../components/ui/Toast';
import { inviteService, getErrorMessage } from '../../../services/apiService';
import { natureLabel, oemOdmLabel, subcategoryLabel, approverNames, needsQualityGate } from '../../../utils/classification';

const formatDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const Row = ({ label, children }) => (
  <div className="grid grid-cols-3 gap-4 py-3 border-b border-slate-100 last:border-0">
    <dt className="text-sm font-semibold text-slate-600">{label}</dt>
    <dd className="col-span-2 text-sm text-slate-900">{children}</dd>
  </div>
);

/**
 * The popup behind an Invite Approval card, like the Level 1 review popup: the invite's details and,
 * for an administrator or a routed approver, Approve and send link / Reject (a reason is required).
 * Everyone else (the colleague who raised it) sees it read-only.
 */
const InviteReviewDialog = ({ invite, onClose, onDecided }) => {
  const toast = useToast();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const approvers = invite.approvers || [];

  const approve = async () => {
    setBusy(true);
    try {
      const res = await inviteService.approveInvite(invite.id);
      const data = res?.data || {};
      if (data.email_sent === false) toast.warning(`Approved, but the e-mail to ${invite.email} failed: ${data.error_message || 'unknown error'}`);
      else toast.success(`Approved. Registration link sent to ${invite.email}`);
      onDecided?.();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await inviteService.rejectInvite(invite.id, reason.trim());
      toast.success('Invite rejected');
      onDecided?.();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="invite-review-dialog">
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 max-w-2xl w-full max-h-[calc(95vh/var(--app-scale))] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-slate-900 truncate">{invite.name}</h2>
            <p className="text-sm text-slate-600 mt-1">Invite review • Awaiting approval{invite.raised_by_me ? ' • Raised by you' : ''}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full p-2 transition-colors">
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          <dl>
            <Row label="Vendor e-mail">{invite.email}</Row>
            <Row label="Nature of business">{natureLabel(invite.nature_of_business)}</Row>
            <Row label="Supplier type">{oemOdmLabel(invite.oem_odm)}</Row>
            <Row label="Sub-category">{subcategoryLabel(invite.supplier_subcategory)}</Row>
            <Row label={approvers.length > 1 ? 'Approvers (any one)' : 'Approver'}>{approverNames(approvers, 'Any administrator')}</Row>
            <Row label="Requested by">
              {invite.raised_by_me ? 'You' : invite.created_by_user?.full_name || invite.created_by_user?.email || 'A colleague'} · {formatDateTime(invite.created_at)}
            </Row>
            {invite.supplier_request_id ? <Row label="From">Supplier request #{invite.supplier_request_id}</Row> : null}
          </dl>

          <div className="mt-5 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex gap-2">
            <Icon name="Info" size={16} className="flex-shrink-0 mt-0.5" />
            <span>
              Approving e-mails the registration link to <strong>{invite.email}</strong>, valid for 7 days.
              {needsQualityGate(invite.supplier_subcategory) && ' After registering, this RM / PM supplier also waits for the quality department before Level 1.'}
              {' '}Whoever raised the invite is told the decision.
            </span>
          </div>

          {rejecting && (
            <div className="mt-5" data-testid="invite-reject-form">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Reason for rejecting <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why should this vendor not be invited?"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-slate-500">The vendor is not contacted. The reason is e-mailed to whoever raised the invite.</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 border-t border-slate-200 flex-shrink-0">
          {!invite.can_decide ? (
            <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2" data-testid="invite-read-only">
              <Icon name="Lock" size={16} />
              <span>Waiting for {approverNames(approvers, 'an administrator')} to decide. You will be e-mailed the outcome.</span>
            </div>
          ) : rejecting ? (
            <>
              <Button variant="outline" onClick={() => { setRejecting(false); setReason(''); }} disabled={busy}>Cancel</Button>
              <Button variant="destructive" onClick={reject} disabled={!reason.trim() || busy} loading={busy} iconName="XCircle" iconPosition="left">
                Confirm rejection
              </Button>
            </>
          ) : (
            <>
              <Button variant="destructive" onClick={() => setRejecting(true)} disabled={busy} iconName="XCircle" iconPosition="left">
                Reject
              </Button>
              <Button variant="success" onClick={approve} disabled={busy} loading={busy} iconName="CheckCircle" iconPosition="left">
                Approve and send link
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteReviewDialog;
